import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, type ReactElement, type ReactNode } from 'react';
import { BrandContext } from '../brandContext';
import { UiProvider } from '../ui/UiProvider';
import { WorkspaceContext, type WorkspaceState } from '../workspaceContext';
import { useBrandContextState } from './brand-state';
import type { createAppRouter } from './routes';
import type { SessionStore } from './session';
import { SessionProvider, useSession } from './session-context';

type AppRouter = ReturnType<typeof createAppRouter>;

export interface AppRootProps {
  session: SessionStore;
  queryClient: QueryClient;
  router: AppRouter;
}

/**
 * The brand and workspace contexts the un-migrated screens still read, both
 * derived from the session. In accounts mode the brand waits for the workspace
 * pin, so nothing workspace-scoped goes out without it.
 */
function SessionContexts({ router, children }: { router: AppRouter; children: ReactNode }): ReactElement {
  const { snapshot } = useSession();
  const { configLoaded, mode, status, workspaceId } = snapshot;
  const brandState = useBrandContextState(
    !configLoaded || (mode === 'accounts' && workspaceId === null) ? 'unknown' : status,
    workspaceId
  );
  const { reloadBrand } = brandState;

  // A move from one workspace to another reloads its brand; entering the
  // first one is covered by the session gate above.
  const previous = useRef(workspaceId);
  useEffect(() => {
    const from = previous.current;
    previous.current = workspaceId;
    if (from !== null && workspaceId !== null && from !== workspaceId) void reloadBrand();
  }, [workspaceId, reloadBrand]);

  // The URL is the workspace's source of truth: switching is navigating.
  const switchTo = useCallback(
    (id: string) => router.navigate({ to: '/w/$workspaceId/reports', params: { workspaceId: id } }),
    [router]
  );
  const workspace = useMemo<WorkspaceState>(() => ({ mode, workspaceId, switchTo }), [mode, workspaceId, switchTo]);

  return (
    <BrandContext.Provider value={brandState}>
      <WorkspaceContext.Provider value={workspace}>{children}</WorkspaceContext.Provider>
    </BrandContext.Provider>
  );
}

/**
 * The store never navigates, and a 401 mid-session never passes through the
 * account menu, so losing the session is handled here: go to sign-in, then
 * empty the query cache so the next person on this tab sees none of it. The
 * store has already remembered the return path; this does not touch it.
 */
function useSignedOutRedirect(session: SessionStore, queryClient: QueryClient, router: AppRouter): void {
  useEffect(() => {
    let previous = session.getSnapshot().status;
    return session.subscribe(() => {
      const status = session.getSnapshot().status;
      const lost = previous === 'signed-in' && status === 'signed-out';
      previous = status;
      if (!lost) return;
      // Cleared after the navigation, so a still-mounted query cannot refetch into the emptied cache.
      void router.navigate({ to: '/login', replace: true }).then(() => queryClient.clear());
    });
  }, [session, queryClient, router]);
}

function portalContainer(): HTMLElement {
  return document.getElementById('root') ?? document.body;
}

/** Every provider the app needs, outermost first, around the router. No side effects on import. */
export function AppRoot({ session, queryClient, router }: AppRootProps): ReactElement {
  useSignedOutRedirect(session, queryClient, router);
  const navigate = useCallback((href: string) => void router.navigate({ href }), [router]);
  return (
    <SessionProvider store={session}>
      <QueryClientProvider client={queryClient}>
        <SessionContexts router={router}>
          <UiProvider navigate={navigate} portalContainer={portalContainer}>
            <RouterProvider router={router} />
          </UiProvider>
        </SessionContexts>
      </QueryClientProvider>
    </SessionProvider>
  );
}
